// docs/17 §2–§3 — Play online: the hub a player lands on with no World chosen. A username (the
// handle friends type), the friend list with requests, invitations to Worlds, the Worlds I belong
// to (Continue / back to the lobby), Worlds I may join (open, or the keeper is my friend), and
// "Found a World" — a lobby to gather friends in first, or a quick World that begins at once.
// docs/37: the player names the lobby only (no seed); lists lead with the lobby name and give the
// storyteller's World name under it, friend status lines say the World's name.
// `FriendsPanel` is reused by the World lobby with `inviteWorldId` to invite from the list; the
// friend status hooks (`useFriendStatus`, `useJoinRules`, `useJoinWorld`) feed the title's
// "Friends online" panel too. Mockup pass (docs/18): over the dimmed night clearing, square plates.
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { OFFLINE_TITLE, useLinkUp } from '../../core/stores/link';
import { usePlace } from '../../core/audio/places';
import { useTable } from 'spacetimedb/react';
import { Badge, Button, ButtonRow, Card, CardList, Display, EmptyState, Eyebrow, Field, HeroSprite, Input, OryxIcon, Panel, Reveal, SkeletonList, useSettled } from '../components';
import { useSession } from '../../core/stores/session';
import { useRoute } from '../../core/stores/route';
import { useAllWorldMembers, useAllWorldSettings, useFriends, useMyAccount, useMyHeroes, useMyProfile, useMyWorldInvites, useProfileMap, useWorlds, type FriendEntry } from '../../core/subscriptions/hooks';
import { tables } from '../../bindings';
import { rpc, useToasts } from '../../core/net/reducers';
import { identityHex, parseIdentity, tsToMs } from '../../core/net/connection';
import { serverNow } from '../../core/time';
import { PLAYER_COLOURS } from '../../game/board/players';
import { classLook } from '../../game/heroKit';
import { CLASS_FRAMES, CLASS_NAMES } from '../../core/sim/adapt';
import { NightClearing, type ClearingHero } from './NightClearing';
import { isNamed, lobbyTitle, worldLine, worldTitle } from '../../core/story/worldName';
import { uncensoredLabel } from '../../core/story/storyteller';
import './online.css';

export const PHASE_GATHERING = 0;
export const VISIBILITY = ['Invitation only', 'Friends of the keeper', 'Open to all'] as const;
const NONE_ID = '0'.repeat(64);

/** Player colour by list position (`--fk-p1..20`, docs/18). */
export const playerColour = (i: number): string => `var(--fk-p${(i % PLAYER_COLOURS.length) + 1})`;

// "New fellowship" on the title asks the hub to focus the lobby-name field once it mounts.
let focusCreateOnMount = false;
export function requestCreateFocus(): void { focusCreateOnMount = true; }

/** A clock for "last seen" and presence freshness, ticking every 15 s. */
export function useNow(every = 15_000): number {
  const [now, setNow] = useState(() => serverNow());
  useEffect(() => { const t = window.setInterval(() => setNow(serverNow()), every); return () => window.clearInterval(t); }, [every]);
  return now;
}

export function ago(ms: number): string {
  const m = Math.floor(ms / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.floor(h / 24);
  return d === 1 ? 'a day ago' : `${d} days ago`;
}

/** Who may join which World (docs/17 §3): my Worlds, open seats, visibility, late join. */
export function useJoinRules() {
  const me = useSession((s) => s.identity ?? '');
  const settings = useAllWorldSettings();
  const account = useMyAccount();
  const members = useAllWorldMembers();
  const friends = useFriends();
  return useMemo(() => {
    const settingsOf = new Map(settings.map((s) => [s.worldId.toString(), s]));
    const seatsOf = new Map<string, number>();
    for (const x of members) seatsOf.set(x.worldId.toString(), (seatsOf.get(x.worldId.toString()) ?? 0) + 1);
    const mine = new Set(account.map((m) => m.worldId.toString()));
    const friendSet = new Set(friends.filter((f) => f.state === 'friend').map((f) => f.other));
    const canJoin = (worldId: bigint, ownerHex: string) => {
      if (mine.has(worldId.toString())) return false;
      const s = settingsOf.get(worldId.toString());
      if (!s) return true;
      if ((seatsOf.get(worldId.toString()) ?? 0) >= s.options.maxPlayers) return false;
      if (s.phase !== PHASE_GATHERING && !s.options.lateJoin) return false;
      return s.options.visibility === 2 || (s.options.visibility === 1 && friendSet.has(ownerHex));
    };
    return { me, settingsOf, seatsOf, mine, canJoin };
  }, [me, settings, account, members, friends]);
}

/** Enter a World I belong to: its lobby while gathering, else the World screen. */
export function useEnterWorld() {
  const setWorld = useSession((s) => s.setWorld);
  const go = useRoute((s) => s.go);
  const settings = useAllWorldSettings();
  return (worldId: bigint) => {
    const s = settings.find((x) => x.worldId === worldId);
    setWorld(worldId);
    go(s?.phase === PHASE_GATHERING ? 'gathering' : 'world', { partyId: undefined, encounterId: undefined, instanceId: undefined });
  };
}

/** Join a World (blank name = my username, server-side) and enter it; enter directly if already mine. */
export function useJoinWorld() {
  const enter = useEnterWorld();
  const { mine } = useJoinRules();
  const [busy, setBusy] = useState<bigint | undefined>();
  const join = async (id: bigint, name = '') => {
    if (mine.has(id.toString())) { enter(id); return; }
    setBusy(id);
    const r = await rpc.joinWorld(id, name.trim());
    setBusy(undefined);
    if (r.ok) enter(id);
  };
  return { join, busy };
}

const SCREEN_STATUS: Record<string, string> = { gathering: 'In the lobby', world: 'In the World', roster: 'In the World', ledger: 'In the World', realm: 'In the World', inventory: 'In the World', levelup: 'In the World', lobby: 'Gathering a party', localmap: 'On the map', encounter: 'In an encounter', aftermath: 'After a fight' };

/** `worldName` is the storyteller's (shown in `line`); `lobbyName` labels the Join button (docs/37). */
export interface FriendStatus { frame?: number; classId?: string; heroId?: bigint; species?: number; line: string; worldId?: bigint; worldName?: string; lobbyName?: string; canJoin: boolean; isMine: boolean }

/** What a friend is doing: their freshest World presence (60 s), their latest hero's sprite, last seen. */
export function useFriendStatus(f: FriendEntry, now: number): FriendStatus {
  const isFriend = f.state === 'friend';
  const id = useMemo(() => parseIdentity(isFriend ? f.other : NONE_ID), [f.other, isFriend]);
  const presence = useTable(tables.worldPresence.where((r) => r.owner.eq(id)), { enabled: isFriend })[0];
  const heroes = useTable(tables.hero.where((r) => r.owner.eq(id)), { enabled: isFriend })[0];
  const worlds = useWorlds();
  const profiles = useProfileMap();
  const rules = useJoinRules();
  return useMemo(() => {
    const fresh = f.online ? [...presence].filter((p) => (tsToMs(p.seenAt) ?? 0) > now - 60_000).sort((a, b) => (tsToMs(b.seenAt) ?? 0) - (tsToMs(a.seenAt) ?? 0))[0] : undefined;
    const world = fresh ? worlds.find((w) => w.id === fresh.worldId) : undefined;
    const hero = [...heroes].sort((a, b) => (world ? Number(b.worldId === world.id) - Number(a.worldId === world.id) : 0) || (a.id > b.id ? -1 : 1))[0];
    const frame = hero ? (hero.oryxFrame || undefined) : undefined;
    const classId = hero ? CLASS_NAMES[hero.class] : undefined;
    const who = { frame, classId, heroId: hero?.id, species: hero?.species };
    if (f.state === 'incoming') return { ...who, line: 'Wants to be friends', canJoin: false, isMine: false };
    if (f.state === 'outgoing') return { ...who, line: 'Asked · waiting for them', canJoin: false, isMine: false };
    if (!f.online) {
      const seen = tsToMs(profiles.get(f.other)?.lastSeen);
      return { ...who, line: seen ? `Offline · ${ago(now - seen)}` : 'Offline', canJoin: false, isMine: false };
    }
    if (!world || !fresh) return { ...who, line: 'Idle', canJoin: false, isMine: false };
    const gathering = rules.settingsOf.get(world.id.toString())?.phase === PHASE_GATHERING;
    const label = gathering ? 'In the lobby' : SCREEN_STATUS[fresh.screen];
    const isMine = rules.mine.has(world.id.toString());
    const canJoin = rules.canJoin(world.id, identityHex(world.owner));
    const lobbyName = lobbyTitle(rules.settingsOf.get(world.id.toString()), world);
    // docs/62: a gathering lobby has no World yet, so its friends see the lobby's name
    const where = isNamed(world) || !gathering ? worldTitle(world) : lobbyName;
    return { ...who, line: label ? `${label} · ${where}` : 'Idle', worldId: world.id, worldName: worldLine(world, gathering), lobbyName, canJoin, isMine };
  }, [f, presence, heroes, worlds, profiles, rules, now]);
}

/** A friend's face: their latest hero at 2×, or their initial in their colour. */
export function FriendAvatar({ status, name, colour }: { status: FriendStatus; name: string; colour: string }) {
  return (
    <span className="fk-online__avatar" style={{ color: colour }}>
      {status.frame || status.classId ? <HeroSprite heroId={status.heroId} look={status.heroId === undefined ? classLook(status.classId) : undefined} species={status.species} frame={status.frame} classId={status.classId} scale={2} title={name} /> : <span className="fk-online__initial" aria-hidden="true">{name.replace(/^@/, '').slice(0, 1).toUpperCase()}</span>}
    </span>
  );
}

/** Claim (or change) the username friends will type. */
export function UsernameCard() {
  const profile = useMyProfile();
  const [name, setName] = useState('');
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const claim = async () => {
    if (!name.trim()) return;
    setBusy(true);
    const r = await rpc.claimUsername(name.trim());
    setBusy(false);
    if (r.ok) { setEditing(false); setName(''); }
  };
  if (profile && !editing) {
    return (
      <div className="fk-online__me" data-testid="my-username" data-username={profile.username}>
        <span className="fk-online__handle">@{profile.username}</span>
        <Button small variant="ghost" onClick={() => { setName(profile.username); setEditing(true); }} data-testid="username-change">Change</Button>
      </div>
    );
  }
  return (
    <div className="fk-online__claim">
      <Field label={profile ? 'New username' : 'Choose a username'} hint="3–20 letters, digits, _ or -; friends add you by it" htmlFor="fk-username">
        <div className="fk-online__inline">
          <Input id="fk-username" data-testid="username-input" placeholder="e.g. mara_of_fordwater" value={name} maxLength={20} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void claim(); }} />
          <Button primary disabled={busy || !name.trim()} loading={busy} onClick={() => void claim()} data-testid="username-claim">{profile ? 'Save' : 'Claim'}</Button>
          {profile && <Button variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>}
        </div>
      </Field>
    </div>
  );
}

/** One friend line — sprite, name in their colour, what they are doing — with the row's buttons. */
export function FriendLine({ f, index, now, testId = 'friend', joinName, children }: { f: FriendEntry; index: number; now: number; testId?: string; joinName?: string; children?: ReactNode }) {
  const status = useFriendStatus(f, now);
  const { join, busy } = useJoinWorld();
  const handle = f.username ? f.username : f.other.slice(-6);
  const colour = f.state === 'friend' && f.online ? playerColour(index) : 'var(--fk-ink-faint)';
  const joinable = status.worldId !== undefined && (status.canJoin || status.isMine);
  return (
    <li className={`fk-online__friend fk-online__friend--${f.state} ${f.online ? '' : 'fk-online__friend--off'}`} data-testid={testId} data-username={f.username} data-state={f.state} data-online={f.online}>
      <FriendAvatar status={status} name={handle} colour={colour} />
      <span className="fk-online__who">
        <span className="fk-online__friend-name" style={{ color: colour }}>{handle}</span>
        <span className="fk-online__status" data-testid="friend-status">{status.line}</span>
      </span>
      <ButtonRow className="fk-online__friend-actions">
        {joinable && <Button small loading={busy === status.worldId} disabled={busy !== undefined} onClick={() => void join(status.worldId!, joinName)} title={`${status.isMine ? 'Enter' : 'Join'} the lobby ${status.lobbyName ?? ''}`} data-testid="friend-join" data-world-name={status.worldName} data-lobby-name={status.lobbyName}>Join</Button>}
        {children}
      </ButtonRow>
    </li>
  );
}

function FriendRow({ f, index, now, inviteWorldId, invited, member }: { f: FriendEntry; index: number; now: number; inviteWorldId?: bigint; invited: boolean; member: boolean }) {
  const [busy, setBusy] = useState<string | undefined>();
  const run = async (key: string, fn: () => Promise<unknown>) => { setBusy(key); await fn(); setBusy(undefined); };
  return (
    <FriendLine f={f} index={index} now={now} testId="friend">
      {f.state === 'incoming' && <>
        <Button small primary loading={busy === 'acc'} disabled={!!busy} onClick={() => void run('acc', () => rpc.respondFriendRequest(f.id, true))} data-testid="friend-accept">Accept</Button>
        <Button small variant="ghost" loading={busy === 'dec'} disabled={!!busy} onClick={() => void run('dec', () => rpc.respondFriendRequest(f.id, false))} data-testid="friend-decline">Decline</Button>
      </>}
      {f.state === 'outgoing' && <Button small variant="ghost" loading={busy === 'wd'} disabled={!!busy} onClick={() => void run('wd', () => rpc.removeFriend(f.id))} data-testid="friend-withdraw">Withdraw</Button>}
      {f.state === 'friend' && inviteWorldId !== undefined && (member
        ? <Badge tone="green">in this World</Badge>
        : invited
          ? <Badge tone="blue">invited</Badge>
          : <Button small primary loading={busy === 'inv'} disabled={!!busy} onClick={() => void run('inv', () => rpc.inviteToWorld(inviteWorldId, f.other))} data-testid="friend-invite">Invite</Button>)}
      {f.state === 'friend' && inviteWorldId === undefined && <Button small variant="ghost" loading={busy === 'rm'} disabled={!!busy} title="Remove friend" onClick={() => void run('rm', () => rpc.removeFriend(f.id))} data-testid="friend-remove">✕</Button>}
    </FriendLine>
  );
}

/** Friends with the add-by-username field; with `inviteWorldId` each friend gets an Invite button. */
export function FriendsPanel({ inviteWorldId, className = '' }: { inviteWorldId?: bigint; className?: string }) {
  const profile = useMyProfile();
  const friends = useFriends();
  const invites = useMyWorldInvites();
  const members = useAllWorldMembers();
  const settled = useSettled(800, 'friends');
  const push = useToasts((s) => s.push);
  const now = useNow();
  const [add, setAdd] = useState('');
  const [busy, setBusy] = useState(false);
  const send = async () => {
    const u = add.trim().replace(/^@/, '');
    if (!u) return;
    setBusy(true);
    const r = await rpc.sendFriendRequest(u);
    setBusy(false);
    if (r.ok) { push(`Asked @${u.toLowerCase()} to be friends.`, 'success'); setAdd(''); }
  };
  const inWorld = new Set(members.filter((m) => m.worldId === inviteWorldId).map((m) => identityHex(m.owner)));
  const invited = new Set(invites.filter((i) => i.worldId === inviteWorldId).map((i) => identityHex(i.to)));
  const incoming = friends.filter((f) => f.state === 'incoming');
  const shown = inviteWorldId !== undefined ? friends.filter((f) => f.state === 'friend') : friends;
  const accepted = friends.filter((f) => f.state === 'friend');
  const online = accepted.filter((f) => f.online).length;
  return (
    <Panel title={inviteWorldId !== undefined ? 'Invite friends' : 'Friends'} label="Friends" className={`fk-online__friends ${className}`} data-testid="friends-panel"
      actions={<>{incoming.length > 0 && <Badge tone="gold" filled pulse>{incoming.length}</Badge>}<span className="fk-online__count" data-online={online}>{online} of {accepted.length} online</span></>}>
      {!profile
        ? <p className="fk-muted fk-online__note">Claim a username first — friends add each other by it.</p>
        : (
          <div className="fk-online__inline fk-online__add">
            <Input data-testid="friend-input" placeholder="add a friend by @username" value={add} onChange={(e) => setAdd(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void send(); }} aria-label="Friend's username" />
            <Button disabled={busy || !add.trim()} loading={busy} onClick={() => void send()} data-testid="friend-add">Add</Button>
          </div>
        )}
      {shown.length === 0
        ? (settled ? <EmptyState glyph="◎" title={inviteWorldId !== undefined ? 'No friends to invite yet' : 'No friends yet'}>{profile ? 'Ask someone for their username and add them here.' : undefined}</EmptyState> : <SkeletonList n={2} kind="text" label="Loading friends" />)
        : <ul className="fk-online__friend-list" data-testid="friend-list">{shown.map((f, i) => <FriendRow key={f.id.toString()} f={f} index={i} now={now} inviteWorldId={inviteWorldId} invited={invited.has(f.other)} member={inWorld.has(f.other)} />)}</ul>}
    </Panel>
  );
}

/** The clearing's heroes: my heroes in the remembered World, padded with the class line-up. */
export function useClearingHeroes(worldId: bigint | undefined): ClearingHero[] {
  const mine = useMyHeroes();
  return useMemo(() => {
    const real: ClearingHero[] = mine.filter((h) => worldId !== undefined && h.worldId === worldId).slice(0, 4).map((h) => ({ key: `h${h.id}`, heroId: h.id, species: h.species, frame: h.oryxFrame || undefined, classId: CLASS_NAMES[h.class], title: h.name }));
    const used = new Set(real.map((h) => h.frame ?? CLASS_FRAMES[h.classId ?? '']));
    const pad = ['fighter', 'ranger', 'wizard', 'cleric', 'rogue', 'paladin', 'druid', 'bard'].filter((c) => !used.has(CLASS_FRAMES[c])).map((c, i) => ({ key: c, classId: c, look: classLook(c, [0, 2, 1, 7, 3, 4, 8, 9][i] ?? 0, i % 2 ? 'female' : 'male') }));
    return [...real, ...pad].slice(0, 4);
  }, [mine, worldId]);
}

export function PlayOnline() {
  const up = useLinkUp();
  usePlace({ key: 'place.title' });   // docs/44: the world list under the same night as the title
  const me = useSession((s) => s.identity ?? '');
  const worldId = useSession((s) => s.worldId);
  const worlds = useWorlds();
  const invites = useMyWorldInvites();
  const profiles = useProfileMap();
  const profile = useMyProfile();
  const settled = useSettled(1000, 'worlds');
  const enter = useEnterWorld();
  const rules = useJoinRules();
  const { join: joinAs, busy: joining } = useJoinWorld();
  const heroes = useClearingHeroes(worldId);
  const [joinName, setJoinName] = useState('');
  const [newLobby, setNewLobby] = useState('');
  const [busy, setBusy] = useState<string | undefined>();
  const [pending, setPending] = useState<Set<string> | undefined>();

  const { settingsOf, seatsOf, mine } = rules;
  const keeperName = (owner: string) => { const p = profiles.get(owner); return owner === me ? 'you' : p ? `@${p.username}` : owner.slice(-6); };
  const sorted = [...worlds].sort((a, b) => (a.id > b.id ? -1 : 1));
  const myWorlds = sorted.filter((w) => mine.has(w.id.toString()));
  const toMe = invites.filter((i) => identityHex(i.to) === me);
  const joinable = sorted.filter((w) => rules.canJoin(w.id, identityHex(w.owner)));

  useEffect(() => {
    if (!focusCreateOnMount) return;
    focusCreateOnMount = false;
    document.getElementById('fk-lobby-name')?.focus({ preventScroll: false });
  }, []);

  // After open_world_lobby / create_world the row arrives in the cache: enter the World I own that I did not before (docs/60: a
  // shelf World was there already, under the module's name).
  useEffect(() => {
    if (!pending) return;
    const made = worlds.find((w) => !pending.has(w.id.toString()) && identityHex(w.owner) === me && settingsOf.has(w.id.toString()));
    if (made) { setPending(undefined); enter(made.id); }
  }, [worlds, pending, me, settingsOf, enter]);

  const found = async (lobby: boolean) => {
    if (!newLobby.trim()) return;
    setBusy(lobby ? 'lobby' : 'quick');
    const before = new Set(worlds.filter((w) => identityHex(w.owner) === me).map((w) => w.id.toString()));
    const r = lobby ? await rpc.openWorldLobby(newLobby.trim()) : await rpc.createWorld(newLobby.trim());
    setBusy(undefined);
    if (r.ok) { setNewLobby(''); setPending(before); }
  };
  const answer = async (id: bigint, worldId: bigint, accept: boolean) => {
    setBusy(`inv-${id}`);
    const r = await rpc.respondWorldInvite(id, accept);
    setBusy(undefined);
    if (r.ok && accept) enter(worldId);
  };

  const phaseBadge = (worldId: bigint) => {
    const s = settingsOf.get(worldId.toString());
    return !s || s.phase !== PHASE_GATHERING ? <Badge tone="green">under way</Badge> : <Badge tone="gold" pulse>gathering</Badge>;
  };
  /** A list card's names (docs/37 LN7): the lobby's leads, the storyteller's World name sits under it. */
  const names = (worldId: bigint, w: { name: string } | undefined) => ({ lobby: lobbyTitle(settingsOf.get(worldId.toString()), w), world: worldLine(w, settingsOf.get(worldId.toString())?.phase === PHASE_GATHERING), told: isNamed(w) });
  const realmLine = (n: { world: string; told: boolean }) => <div className={`fk-card__sub fk-online__realm ${n.told ? '' : 'fk-online__realm--untold'}`} data-testid="world-realm">{n.told ? <>World of <b>{n.world}</b></> : n.world}</div>;
  const seats = (worldId: bigint) => {
    const s = settingsOf.get(worldId.toString());
    const n = seatsOf.get(worldId.toString()) ?? 0;
    return s ? `${n} / ${s.options.maxPlayers} seats` : `${n} member${n === 1 ? '' : 's'}`;
  };

  return (
    <main className="fk-screen fk-screen--online fk-hit" data-testid="world-screen">
      <NightClearing heroes={heroes} dim className="fk-online__backdrop" />
      <div className="fk-online">
        <Reveal className="fk-online__head">
          <Eyebrow>Fellowkeep</Eyebrow>
          <Display size="lg" gold as="h1" className="fk-online__title">Play online</Display>
          <p className="fk-online__tag"><span>Add friends by username, gather them in a World's lobby, or pick up a World you belong to.</span></p>
        </Reveal>

        <div className="fk-online__left">
          <Panel title="You" label="You" rise className="fk-online__you">
            <UsernameCard />
          </Panel>
          <FriendsPanel />
        </div>

        <div className="fk-online__centre">
          {toMe.length > 0 && (
            <Panel title="Invitations" label="Invitations" rise glow actions={<Badge tone="gold" filled>{toMe.length}</Badge>}>
              <CardList data-testid="world-invites" className="fk-online__plates">
                {toMe.map((i, k) => {
                  const w = worlds.find((x) => x.id === i.worldId);
                  const from = profiles.get(identityHex(i.from));
                  const n = names(i.worldId, w);
                  return (
                    <Card key={i.id.toString()} index={k} className="fk-online__world" data-testid="world-invite" data-lobby-name={n.lobby} data-world-name={n.world}>
                      <OryxIcon name="item_scroll" scale={2} title="Invitation" />
                      <div className="fk-card__body">
                        <div className="fk-card__title">{w ? n.lobby : `World #${i.worldId}`}{phaseBadge(i.worldId)}{uncensoredLabel(w) && <Badge tone="red" title="An uncensored storyteller writes this World">{uncensoredLabel(w)}</Badge>}</div>
                        {w && realmLine(n)}
                        <div className="fk-card__sub">from {from ? `@${from.username}` : identityHex(i.from).slice(-6)} · {seats(i.worldId)}</div>
                      </div>
                      <ButtonRow className="fk-card__aside">
                        <Button primary small loading={busy === `inv-${i.id}`} disabled={!!busy} onClick={() => void answer(i.id, i.worldId, true)} data-testid="invite-accept">Join</Button>
                        <Button small variant="ghost" disabled={!!busy} onClick={() => void answer(i.id, i.worldId, false)} data-testid="invite-decline">Decline</Button>
                      </ButtonRow>
                    </Card>
                  );
                })}
              </CardList>
            </Panel>
          )}

          <Panel title="Your Worlds" label="Your Worlds" rise actions={<span className="fk-online__count">{myWorlds.length}</span>}>
            <CardList data-testid="my-worlds" className="fk-online__plates">
              {myWorlds.length === 0 && (settled ? <EmptyState glyph="✦" title="No Worlds yet">Found one on the right, or accept an invitation.</EmptyState> : <SkeletonList n={2} label="Loading Worlds" />)}
              {myWorlds.map((w, k) => {
                const gathering = settingsOf.get(w.id.toString())?.phase === PHASE_GATHERING;
                const n = names(w.id, w);
                return (
                  <Card key={w.id.toString()} index={k} className="fk-online__world fk-online__world--mine" data-testid="my-world" data-lobby-name={n.lobby} data-world-name={n.world}>
                    <OryxIcon name="chest_gold_closed" scale={2} title="World" />
                    <div className="fk-card__body">
                      <div className="fk-card__title">{n.lobby}{phaseBadge(w.id)}{uncensoredLabel(w) && <Badge tone="red" title="An uncensored storyteller writes this World">{uncensoredLabel(w)}</Badge>}{identityHex(w.owner) === me && <Badge tone="plum">keeper</Badge>}</div>
                      {realmLine(n)}
                      <div className="fk-card__sub">kept by {keeperName(identityHex(w.owner))} · {seats(w.id)}</div>
                    </div>
                    <div className="fk-card__aside"><Button primary small onClick={() => enter(w.id)} data-testid="my-world-enter">{gathering ? 'To the lobby' : 'Continue'}</Button></div>
                  </Card>
                );
              })}
            </CardList>
          </Panel>

          <Panel title="Worlds you can join" label="Joinable Worlds" rise actions={<span className="fk-online__count">{joinable.length}</span>}>
            <Field row label="Your name there" hint={profile ? `blank = @${profile.username}` : undefined} htmlFor="fk-join-name" className="fk-online__join-name">
              <Input id="fk-join-name" data-testid="join-name" value={joinName} onChange={(e) => setJoinName(e.target.value)} placeholder="display name" />
            </Field>
            <CardList data-testid="world-list" className="fk-online__plates">
              {joinable.length === 0 && (settled ? <EmptyState glyph="◌" title="Nothing open right now">Open Worlds and your friends' lobbies show up here.</EmptyState> : <SkeletonList n={2} label="Loading Worlds" />)}
              {joinable.map((w, k) => {
                const s = settingsOf.get(w.id.toString());
                const n = names(w.id, w);
                return (
                  <Card key={w.id.toString()} index={Math.min(k, 8)} className="fk-online__world" data-testid="world-item" data-lobby-name={n.lobby} data-world-name={n.world}>
                    <OryxIcon name="chest_gold_closed" scale={2} title="World" />
                    <div className="fk-card__body">
                      <div className="fk-card__title">{n.lobby}{phaseBadge(w.id)}{uncensoredLabel(w) && <Badge tone="red" title="An uncensored storyteller writes this World">{uncensoredLabel(w)}</Badge>}{s && <Badge tone="grey">{VISIBILITY[s.options.visibility]}</Badge>}</div>
                      {realmLine(n)}
                      <div className="fk-card__sub">kept by {keeperName(identityHex(w.owner))} · {seats(w.id)}{s?.options.description ? ` · ${s.options.description}` : ''}</div>
                    </div>
                    <div className="fk-card__aside"><Button small disabled={joining !== undefined} loading={joining === w.id} onClick={() => void joinAs(w.id, joinName)} data-testid="world-join">Join</Button></div>
                  </Card>
                );
              })}
            </CardList>
          </Panel>
        </div>

        <div className="fk-online__right">
          <Panel title="Found a World" label="Found a World" rise className="fk-online__found">
            <p className="fk-muted fk-online__note">Open a lobby to invite friends, set the World's options together and make your heroes before it begins — or start a World at once and let anyone wander in. The storyteller names the World itself.</p>
            <div className="fk-online__form">
              <Field label="Lobby name" hint="friends find your World by this name; you can change it later" htmlFor="fk-lobby-name"><Input id="fk-lobby-name" data-testid="world-create-name" placeholder="e.g. Friday night crew" maxLength={40} value={newLobby} onChange={(e) => setNewLobby(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void found(true); }} /></Field>
              <Button primary size="lg" block disabled={!up || !!busy || !newLobby.trim()} loading={busy === 'lobby'} title={up ? undefined : OFFLINE_TITLE} onClick={() => void found(true)} data-testid="world-open-lobby">Open a lobby</Button>
              <Button block variant="secondary" disabled={!up || !!busy || !newLobby.trim()} loading={busy === 'quick'} onClick={() => void found(false)} data-testid="world-create" title="Begins at once, open to all, default options">Quick start</Button>
            </div>
          </Panel>
          <p className="fk-online__foot">Your identity is issued by the server and kept in this browser; your username is how friends find you.</p>
        </div>
      </div>
    </main>
  );
}
